import React, {useState, useRef, useCallback, useEffect} from 'react';
import { useIntl, history, useSelector } from 'umi';
import { Button, Justify, Layout, SearchBox, Modal } from 'tea-component';
import classnames from 'classnames';

import { DATALAB_EDITOR, SYSTEM_NAME } from '@dbl/core/global';
import UploadModalBody from './components/UploadModalBody';
import PersonalDataTable from './components/PersonalDataTable';
import { IPersonalDataTableRef } from './components/PersonalDataTable/types';
import { AuthPermissionDisablesContainers } from '../../WorkBench/MyDashboard';
import SvgIcon from "@home/components/SvgIcon";
import { IUploadFileRef } from './components/UploadFile/types';
import {buriedPoint} from "@home/utils/buriedPoint";
import {IFileState, TState} from "@/pages/datalab/pages/Number/PersonalData/components/UploadTableFile/types";
import {ITableInfo} from "@/pages/datalab/pages/Number/PersonalData/components/UploadModalBody/types";
import './index.scss';

const { Content } = Layout;
interface TAllState {
  uploadTableFile: (TState & IFileState) | null;
  saveTableInfo: ITableInfo[] | null;
}
const PersonalData: React.FC = () => {
  
  const { gameCode } = useSelector((state: any) => ({
    gameCode: state.datalab.gameCode,
  }));
  const query = history.location.query as any;

  const [searchWords, setSearchWords] = useState<string | undefined>(query?.keywords ?? '');
  const [searchValue, setSearchValue] = useState<string | undefined>(query?.keywords ?? '');
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [showUploadModal, setShowUploadModal] = useState<boolean>(false);

  const { formatMessage } = useIntl();

  // 新增：表格组件引用
  const tableRef = useRef<IPersonalDataTableRef>(null);
  // 新增：上传文件组件引用
  const uploadFileRef = useRef<IUploadFileRef>(null);
  
  // 创建allStateRef存储所有状态
  const allStateRef = useRef<TAllState>({
    uploadTableFile: null,
    saveTableInfo: null
  });
  
  // 新增：处理上传成功
  const handleUploadSuccess = useCallback(() => {
    // 关闭上传弹窗
    setShowUploadModal(false);
    // 触发表格刷新
    if (tableRef.current) {
      tableRef.current.refreshTable();
    }
  }, []);

  // 新增：处理Modal关闭事件
  const handleModalClose = useCallback(() => {
    // @ts-ignore
    if (['idle', 'error'].includes(uploadFileRef.current?.status)) {
      // 还没有上传文件, 或上传文件失败, 直接关闭弹窗
      setShowUploadModal(false);
      return;
    }
    // 上传文件中, 或上传文件成功弹出弹窗确认
    Modal.confirm({
      message: formatMessage({id: 'dbl_personal_data_close_title'}),
      description: formatMessage({id: 'dbl_personal_data_close_content'}),
      okText: formatMessage({id: 'dbl_common_confirm'}),
      cancelText: formatMessage({id: 'dbl_common_cancel'}),
      style: {zIndex: 1010},
    }).then((result) => {
      // 用户点击了确认按钮
      // 在关闭前调用deleteFile方法
      if (result) {
        uploadFileRef.current?.deleteFile();
        // 如果上传excel文件中 有一部分sheet页面保存成功, 一部分失败, 那么也刷新 列表
        if (allStateRef.current?.saveTableInfo?.some(info => info?.saveStatus === 'success')) {
          handleUploadSuccess();
          return;
        }
        setShowUploadModal(false);
      }
    });
    
  }, []);
  
  useEffect(() => {
    buriedPoint({
      pageId: '602301',
      pageName: '个人数据',
      pagePath: '/DataManagement/PersonalData',
      extInfo: `{"pageName": "Personal Data", "pageNameCn": "个人数据", "id": ""}`,
      uid: gameCode,
      typeId: 'datalab_pc_v2',
      menuId: '0',
    });
  }, [gameCode]);

  return (
    <Content>
      <Content.Header title={formatMessage({ id: 'dbl_personal_data_title' })} />
      <Content.Body full className="dbl-personal-data">
        <Justify
          className={classnames('number_package_buttom')}
          left={
            <div>
              <AuthPermissionDisablesContainers
                isShow
                type={DATALAB_EDITOR.AddChartBtn}
                customDisabled={selectedKeys.length < 1}
              >
                <Button
                  type="primary"
                  onClick={() => {
                    const tableIds = selectedKeys.join(',');
                    history.push(`/${SYSTEM_NAME}/${gameCode}/Analysis/MultiAnalysis?tableId=${tableIds}`);
                  }}
                >
                  {formatMessage({ id: 'dbl_personal_data_enter_analysis' })}
                </Button>
              </AuthPermissionDisablesContainers>
            
              <Button onClick={() => setShowUploadModal(true)} className="dbl-personal-data__upload-btn">
                <SvgIcon className='dbl-btn-top-icon' type="upload" />
                {formatMessage({ id: 'dbl_personal_data_upload_data' })}
              </Button>
            </div>
          
          }
          right={
            <SearchBox
              style={{width: 440}}
              value={searchValue}
              onChange={(value: string) => {
                setSearchValue(value);
                if (!value) setSearchWords('');
              }}
              onSearch={(keyword?: string) => {
                setSearchWords(keyword);
              }}
              onClear={() => {
                setSearchWords('');
              }}
              placeholder={formatMessage({ id: 'dbl_personal_data_search' })}
            />
          }
        />
        <PersonalDataTable
          ref={tableRef}
          searchWords={searchWords}
          selectedKeys={selectedKeys}
          setSelectedKeys={(selectKeys: string[]) => {
            setSelectedKeys(selectKeys);
            return selectKeys;
          }}
        />

        <Modal 
          visible={showUploadModal} 
          caption={formatMessage({ id: 'dbl_personal_data_upload_data' })} 
          onClose={handleModalClose} 
          size={1000}
          className={'dbl-personal-data-modal'}
        >
          <Modal.Body>
            <UploadModalBody
              ref={uploadFileRef}
              style={{
                height: 705
              }}
              allStateRef={allStateRef}
              onClose={() => setShowUploadModal(false)}
              onSaveSuccess={handleUploadSuccess}
            />
          </Modal.Body>
        </Modal>
      </Content.Body>
    </Content>
  );
};

export default PersonalData;
